import React, { useState } from 'react';
import { styled } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { Colors } from 'library';
import { Card, Link, Rating, Row, Stack, Text } from 'library/ui';
import { format12HourTime, formatShortWeekdayMonthDay } from 'shared/Dates';
import { IBusinessJobDetailsModel } from '../../BusinessJob/models';
import ProviderDisplay from './ProviderDisplay';
import CardInfo from './CardInfo';
import HourReportInfo from './HourReportInfo';
import ShiftCardMenu from './ShiftCardMenu';
import { isAfter, startOfTomorrow, isSameDay, addMinutes } from 'date-fns';
import RemoveProviderFromShfit from './RemoveProviderFromShfit';
import ActionRequired from './ActionRequired';
import { useHistory } from 'react-router';
import BanProviderAction from './BanProviderAction';
import CancelShiftModalTrigger from './CancelShiftModalTrigger';
import { BusinessSlot, ShiftJob, ShiftProviderUser } from 'models';
import MarkNoShowButton from './MarkNoShowButton';
import ClockInButton from './ClockInButton';
import CardRequestedTimeChange from './CardRequestedTimeChange';
import { getShiftTimes } from './shiftTimesUtils';
import ComplianceActions from './ComplianceActions';
import { EditPay } from './EditPay';

export default function ShiftBaseCard({
    provider,
    pay,
    status,
    provider_id,
    ongoing_request,
    slot,
    refresh,
    provider_action_required,
    review_score,
    needs_center_action,
    needs718bFiling,
}: {
    provider?: ShiftProviderUser;
    pay?: number | null;
    status: string;
    provider_id: number;
    slot: BusinessSlot;
    ongoing_request: IBusinessJobDetailsModel | ShiftJob;
    provider_action_required?: boolean;
    refresh: () => void;
    review_score?: number;
    needs_center_action?: string[];
    needs718bFiling?: boolean;
}) {
    const history = useHistory();
    const shift = slot.appointments.length ? slot.appointments[0] : undefined;
    const hourReport = shift?.hour_reports?.find((r) => r.provider.id === provider_id);
    const [banModal, setBanModal] = useState(false);
    const { shiftStart, shiftEnd, infoText } = getShiftTimes(slot);

    const clockedInTime =
        hourReport && hourReport.start && !hourReport.end
            ? format12HourTime(hourReport.start, ongoing_request.timezone)
            : undefined;
    const completed = status === 'COMPLETED';

    const showRemoveProvider =
        ['APPROVED', 'ON_STAFF'].includes(status) && isAfter(new Date(slot.start_date), startOfTomorrow());

    const shouldRenderCancelShiftTrigger =
        (showRemoveProvider || isSameDay(new Date(slot.start_date), new Date())) && !hourReport?.start;

    const businessLocationId =
        'address' in ongoing_request
            ? ongoing_request?.address?.business_location
            : ongoing_request.business_location.id;

    const showManageShiftSection =
        !hourReport?.start &&
        isAfter(new Date(), new Date(slot.start_date)) &&
        isSameDay(new Date(slot.start_date), new Date());
    const isThirtyMinAfterStart = isAfter(new Date(), addMinutes(new Date(slot.start_date), 30));

    const showRemoveSection = showRemoveProvider && !!provider && !!shift;
    const showCancelTrigger = shouldRenderCancelShiftTrigger && !!provider;

    function renderManageShiftSection() {
        if (!showManageShiftSection) {
            return null;
        }
        return (
            <Row gap="sm" align="center" wrap>
                {isThirtyMinAfterStart && shift ? (
                    <MarkNoShowButton shift={shift} provider={provider} onComplete={refresh} providerId={provider_id} />
                ) : null}
                {shift && (
                    <ClockInButton
                        appointmentId={shift.id}
                        startDate={shift.start_date}
                        timezone={ongoing_request.timezone}
                        providerName={provider?.first_name || 'Worker'}
                        providerUserId={provider?.id || 0}
                        onComplete={refresh}
                    />
                )}
            </Row>
        );
    }

    function renderReviewAction() {
        if (!completed) {
            return null;
        }
        return review_score ? (
            <Rating value={review_score} readOnly />
        ) : (
            <Link
                onClick={() => history.push(`/providers/review/${provider?.id}`)}
                icon={<StarIcon sx={{ fontSize: 18 }} />}
            >
                Leave a review
            </Link>
        );
    }

    return (
        <ShiftCard completed={completed ?? false}>
            <MenuPosition>
                <ShiftCardMenu
                    provider_id={provider_id}
                    provider={provider}
                    slot={slot}
                    ongoing_request={ongoing_request}
                    status={status}
                    refresh={refresh}
                    hourReport={hourReport}
                    needs718bFiling={needs718bFiling}
                    businessLocationId={businessLocationId}
                />
            </MenuPosition>
            <Stack gap="sm">
                <Row justify="between" align="start" gap="sm">
                    <Stack gap="none" grow>
                        {provider ? (
                            <ProviderDisplay provider={provider} clockedInTime={clockedInTime} />
                        ) : (
                            <Text weight="bold">{formatShortWeekdayMonthDay(slot.start_date)}</Text>
                        )}
                    </Stack>
                </Row>
                <ComplianceActions
                    needs_center_action={needs_center_action}
                    business_location_id={businessLocationId}
                    refresh={refresh}
                    provider_user={provider}
                    has_completed_shift={completed}
                />
                <Stack gap="sm">
                    {shift?.center_initiated_appointment_time_change_request && (
                        <CardRequestedTimeChange
                            type="center"
                            timeChangeRequest={shift.center_initiated_appointment_time_change_request}
                            refresh={refresh}
                            timezone={ongoing_request.timezone}
                        />
                    )}
                    {shift?.worker_initiated_appointment_time_change_request && (
                        <CardRequestedTimeChange
                            type="worker"
                            timeChangeRequest={shift.worker_initiated_appointment_time_change_request}
                            refresh={refresh}
                            timezone={ongoing_request.timezone}
                        />
                    )}
                    <CardInfo
                        title={ongoing_request.headline}
                        content={`${format12HourTime(shiftStart, ongoing_request.timezone)} - ${format12HourTime(shiftEnd, ongoing_request.timezone, true)}`}
                        infoText={infoText}
                        bodyTextBold={false}
                        titleTextBold={true}
                    />
                    <Row gap="sm" align="center">
                        {pay ? <CardInfo bodyTextBold={false} content={`$${pay}/hr`} /> : null}
                        <EditPay
                            visible={!completed}
                            pay={pay}
                            jobId={ongoing_request.id}
                            provider={provider}
                            refresh={refresh}
                        />
                    </Row>
                    {hourReport && provider ? (
                        <HourReportInfo hourReport={hourReport} timezone={ongoing_request.timezone} />
                    ) : null}
                </Stack>
                {provider_action_required ? <ActionRequired /> : null}
                {renderManageShiftSection()}
                {renderReviewAction()}
                {(showRemoveSection || showCancelTrigger) && (
                    <Row gap="sm" align="center" wrap>
                        {showRemoveSection && provider && shift ? (
                            <>
                                <RemoveProviderFromShfit
                                    shift={shift}
                                    provider={provider}
                                    provider_id={provider_id}
                                    setBanModal={setBanModal}
                                />
                                <BanProviderAction
                                    provider={provider}
                                    open={banModal}
                                    onClose={() => {
                                        setBanModal(false);
                                        refresh();
                                    }}
                                />
                            </>
                        ) : null}
                        {showCancelTrigger && provider && (
                            <CancelShiftModalTrigger
                                providerId={provider_id}
                                provider={provider}
                                slot={slot}
                                refresh={refresh}
                            />
                        )}
                    </Row>
                )}
            </Stack>
        </ShiftCard>
    );
}

const ShiftCard = styled(Card)(({ completed }: { completed: boolean }) => ({
    position: 'relative',
    backgroundColor: Colors.white,
    borderLeftColor: completed ? Colors.border : Colors.secondary,
    borderLeftWidth: 5,
    borderLeftStyle: 'solid',
}));

const MenuPosition = styled('div')({
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
});
